import React, { useState } from 'react';
import { Table, Card, Form, InputGroup, Badge, Alert } from 'react-bootstrap';
import { searchProducts } from '../services/priceService';
import ReportButton from './ReportButton';

const ComparisonResults = ({ comparisonData, store1Name, store2Name, store1Id, store2Id }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('percentageDifference');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Filter data based on search term
  const filteredData = searchProducts(comparisonData, searchTerm);
  
  // Sort data based on selected criteria
  const sortedData = [...filteredData].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'itemCode':
        comparison = a.itemCode.localeCompare(b.itemCode);
        break;
      case 'itemName':
        comparison = a.itemName.localeCompare(b.itemName);
        break;
      case 'store1Price':
        comparison = (a.store1Price || 0) - (b.store1Price || 0);
        break;
      case 'store2Price':
        comparison = (a.store2Price || 0) - (b.store2Price || 0);
        break;
      case 'priceDifference':
        comparison = Math.abs(a.priceDifference || 0) - Math.abs(b.priceDifference || 0);
        break;
      case 'percentageDifference':
      default:
        comparison = Math.abs(a.percentageDifference || 0) - Math.abs(b.percentageDifference || 0);
        break;
    }
    
    return sortDirection === 'asc' ? comparison : -comparison;
  });
  
  const handleSortChange = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to descending
      setSortBy(column);
      setSortDirection('desc');
    }
  };
  
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };
  
  if (!comparisonData || comparisonData.length === 0) {
    return (
      <Alert variant="info">
        No matching products found between the selected stores.
      </Alert>
    );
  }
  
  return (
    <Card>
      <Card.Header>
        <h4 className="mb-0" style={{ fontSize: 'var(--font-title)', fontWeight: 600, letterSpacing: '-0.3px' }}>Price Comparison Results</h4>
      </Card.Header>
      <Card.Body>
        <div className="mb-3">
          <InputGroup>
            <InputGroup.Text>Search Products</InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Enter product name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>
        
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr className="bg-light">
                <th 
                  onClick={() => handleSortChange('itemCode')}
                  style={{ cursor: 'pointer' }}
                >
                  Item Code {renderSortIcon('itemCode')}
                </th>
                <th 
                  onClick={() => handleSortChange('itemName')}
                  style={{ cursor: 'pointer' }}
                >
                  Product Name {renderSortIcon('itemName')}
                </th>
                <th 
                  onClick={() => handleSortChange('store1Price')}
                  style={{ cursor: 'pointer' }}
                >
                  {store1Name} Price (₪) {renderSortIcon('store1Price')}
                </th>
                <th 
                  onClick={() => handleSortChange('store2Price')}
                  style={{ cursor: 'pointer' }}
                >
                  {store2Name} Price (₪) {renderSortIcon('store2Price')}
                </th>
                <th 
                  onClick={() => handleSortChange('priceDifference')}
                  style={{ cursor: 'pointer' }}
                >
                  Price Difference (₪) {renderSortIcon('priceDifference')}
                </th>
                <th 
                  onClick={() => handleSortChange('percentageDifference')}
                  style={{ cursor: 'pointer' }}
                >
                  % Difference {renderSortIcon('percentageDifference')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((item) => {
                const [s1chain, s1id] = store1Id ? store1Id.split(/_(.+)/) : ['', ''];
                const [s2chain, s2id] = store2Id ? store2Id.split(/_(.+)/) : ['', ''];
                return (
                <tr key={item.itemCode}>
                  <td>{item.itemCode}</td>
                  <td dir="rtl">{item.itemName}</td>
                  <td>
                    {item.store1Price != null ? (
                      <>{item.store1Price.toFixed(2)}<ReportButton itemCode={item.itemCode} storeId={s1id} chain={s1chain} price={item.store1Price} itemName={item.itemName} /></>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {item.store2Price != null ? (
                      <>{item.store2Price.toFixed(2)}<ReportButton itemCode={item.itemCode} storeId={s2id} chain={s2chain} price={item.store2Price} itemName={item.itemName} /></>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {item.priceDifference !== null ? (
                      <span style={{ color: item.priceDifference < 0 ? 'var(--apple-accent)' : '#1c7a3b' }}>
                        {Math.abs(item.priceDifference).toFixed(2)}
                        {item.priceDifference < 0 ? ' (cheaper at ' + store1Name + ')' : ' (cheaper at ' + store2Name + ')'}
                      </span>
                    ) : 'N/A'}
                  </td>
                  <td>
                    {item.percentageDifference !== null && (
                      <Badge
                        bg={Math.abs(item.percentageDifference) > 20 ? 'danger' :
                           Math.abs(item.percentageDifference) > 10 ? 'warning' : 'info'}
                        className={Math.abs(item.percentageDifference) > 20 ? 'diff-badge-high' :
                                   Math.abs(item.percentageDifference) > 10 ? 'diff-badge-med' : 'diff-badge-low'}
                      >
                        {Math.abs(item.percentageDifference).toFixed(2)}%
                      </Badge>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
        
        <div className="mt-3">
          <p><strong>Total products compared:</strong> {sortedData.length}</p>
          <p><strong>Products cheaper at {store1Name}:</strong> {sortedData.filter(item => item.priceDifference < 0).length}</p>
          <p><strong>Products cheaper at {store2Name}:</strong> {sortedData.filter(item => item.priceDifference > 0).length}</p>
          <p><strong>Products with same price:</strong> {sortedData.filter(item => item.priceDifference === 0).length}</p>
        </div>
      </Card.Body>
    </Card>
  );
};

export default ComparisonResults;
